import type { EditorEvent, EditorFileRecord, RequestPhase } from "@/lib/compiler/types"

export type SynctexForwardBox = {
  page: number
  x: number
  y: number
  width: number
  height: number
  sourceLine?: number
  sourceColumn?: number
  occurrenceIndex?: number
}

export const TAB_SYNC_CHANNEL = "rudrise-tab-sync"
export const DETACHED_TAB_STORAGE_KEY = "rudrise-detached-tab"
export const DETACHED_WINDOW_STORAGE_KEY = "rudrise-detached-window"
export const DETACHED_TAB_DRAG_MIME = "application/x-rudrise-tab"

export type DetachableTabType = "editor" | "pdf" | "epub" | "console"

export interface DetachableTabData {
  id: string
  type: DetachableTabType
  name: string
  component?: string
  fileId?: string
  compiledFile?: string
  pdfUrl?: string
  sessionId?: string
  projectId?: string
  content?: string
  config?: Record<string, unknown>
}

export interface DetachedWindowData {
  id: string
  tabs: DetachableTabData[]
  activeTabId: string
}

export interface DetachedStateSnapshot {
  sessionId?: string
  sessionPhase: RequestPhase
  mainFile: string
  activeFile?: string
  autoCompile: boolean
  files: Record<string, EditorFileRecord>
  events: EditorEvent[]
  pdfUrl?: string
  pdfByFile: Record<string, string>
  compiledFile?: string
  compileStatus: "idle" | "compiling" | "error"
  compileError?: string
  compileErrorDetails?: string
  wsStatus: "disconnected" | "connecting" | "connected" | "reconnecting"
}

/** Messages broadcast between main editor and detached windows. */
export type TabSyncMessage =
  | { type: "TAB_DETACHED"; tabId: string }
  | { type: "DETACHED_READY"; tabId: string }
  | { type: "DETACHED_CLOSED"; tabId: string }
  | { type: "DETACHED_WINDOW_READY"; windowId: string; tabIds: string[] }
  | { type: "DETACHED_WINDOW_CLOSED"; windowId: string }
  | { type: "TAB_ADD_TO_WINDOW"; windowId: string; tab: DetachableTabData }
  | { type: "STATE_SYNC"; tabId: string; snapshot: DetachedStateSnapshot }
  | { type: "FILE_EDIT"; sourceTabId: string; fileId: string; content: string }
  | { type: "SET_ACTIVE_FILE"; sourceTabId: string; fileId: string }
  | { type: "MANUAL_COMPILE"; sourceTabId: string }
  | { type: "SYNCTEX_FORWARD"; sourceTabId: string; box: SynctexForwardBox }
  | {
      type: "SYNCTEX_INVERSE"
      sourceTabId: string
      line: number
      column: number
      word?: string
      fileId?: string
    }
  | {
      type: "OPEN_FILE_AT_LINE"
      sourceTabId: string
      fileId: string
      fileName: string
      line: number
    }

/** @deprecated Use buildDetachedWindowUrl for multi-tab detached windows. */
export function buildDetachedTabUrl(tab: Pick<DetachableTabData, "type" | "id">): string {
  const params = new URLSearchParams({
    type: tab.type,
    id: tab.id,
  })
  return `/editor/detached?${params.toString()}`
}

export function buildDetachedWindowUrl(windowId: string): string {
  return `/editor/detached?window=${encodeURIComponent(windowId)}`
}

export function detachedWindowStorageKey(windowId: string): string {
  return `${DETACHED_WINDOW_STORAGE_KEY}-${windowId}`
}

export function generateDetachedWindowId(): string {
  return `win-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`
}

export function detachedWindowName(windowId: string): string {
  return `rudrise-detached-${windowId}`
}

export function parseDetachedTabUrl(
  searchParams: URLSearchParams,
): {
  windowId: string | null
  type: DetachableTabType | null
  id: string | null
} {
  const windowId = searchParams.get("window")
  const type = searchParams.get("type") as DetachableTabType | null
  const id = searchParams.get("id")
  return { windowId, type, id }
}

export function detachedTabStorageKey(tabId: string): string {
  return `${DETACHED_TAB_STORAGE_KEY}-${tabId}`
}

export function createTabSyncChannel(): BroadcastChannel | null {
  if (typeof window === "undefined") return null
  try {
    return new BroadcastChannel(TAB_SYNC_CHANNEL)
  } catch {
    return null
  }
}

export function postTabSyncMessage(message: TabSyncMessage): void {
  const channel = createTabSyncChannel()
  if (!channel) return
  channel.postMessage(message)
  channel.close()
}

export function saveDetachedTabData(tab: DetachableTabData): void {
  if (typeof window === "undefined") return
  try {
    const payload = JSON.stringify(tab)
    localStorage.setItem(detachedTabStorageKey(tab.id), payload)
    localStorage.setItem(DETACHED_TAB_STORAGE_KEY, payload)
  } catch {
    // quota exceeded — ignore
  }
}

export function loadDetachedTabData(tabId: string): DetachableTabData | null {
  if (typeof window === "undefined") return null
  try {
    const perTab = localStorage.getItem(detachedTabStorageKey(tabId))
    if (perTab) return JSON.parse(perTab) as DetachableTabData

    const legacy = localStorage.getItem(DETACHED_TAB_STORAGE_KEY)
    if (!legacy) return null
    const data = JSON.parse(legacy) as DetachableTabData
    return data.id === tabId ? data : null
  } catch {
    return null
  }
}

export function saveDetachedWindowData(data: DetachedWindowData): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(detachedWindowStorageKey(data.id), JSON.stringify(data))
    for (const tab of data.tabs) {
      saveDetachedTabData(tab)
    }
  } catch {
    // quota exceeded — ignore
  }
}

export function loadDetachedWindowData(windowId: string): DetachedWindowData | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(detachedWindowStorageKey(windowId))
    if (!raw) return null
    return JSON.parse(raw) as DetachedWindowData
  } catch {
    return null
  }
}

/** Build a single-tab window payload from legacy ?type=&id= URLs. */
export function detachedWindowFromLegacyTab(
  tab: DetachableTabData,
): DetachedWindowData {
  const windowId = generateDetachedWindowId()
  return { id: windowId, tabs: [tab], activeTabId: tab.id }
}
